import { useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { IDashboardBehaviorSelection } from "ts/interfaces/AdminDashboard"

import {
  behaviorLabel,
  isSameBehavior,
  RAW_INTERPOLATION,
} from "./behaviorFormat"

// Space left between the sticky header and the scrolled-to chart.
const SCROLL_GAP = 16

// Where focus, scroll and the announcement go when the selection changes.
const useBehaviorSelectionFocus = ({
  selection,
  select,
  clear,
  closeDialogs,
}: {
  selection: IDashboardBehaviorSelection | null
  select: (behavior: IDashboardBehaviorSelection) => void
  clear: () => void
  closeDialogs: () => void
}) => {
  const { t } = useTranslation()
  const titleRef = useRef<HTMLHeadingElement>(null)
  const chartAreaRef = useRef<HTMLDivElement>(null)
  const clearButtonRef = useRef<HTMLButtonElement>(null)
  // The selected behaviour's row in the inline lists, if it is there.
  const selectedRowRef = useRef<HTMLButtonElement>(null)
  // Where focus goes once the dialogs closed by a row press have gone.
  const focusAfterDialogs = useRef<"card" | "title" | null>(null)
  // Set only by a press in this session, never on load or a period change.
  const [announcement, setAnnouncement] = useState("")

  const clearSelection = () => {
    clear()
    setAnnouncement(t("adminDashboard.chartShowsTotals"))
  }

  // The card goes away, so focus moves to the row it described, else to the
  // section title.
  const handleClear = () => {
    if (!selection) return
    const target = selectedRowRef.current ?? titleRef.current
    target?.focus()
    clearSelection()
  }

  // Unless the whole chart is already on screen, it scrolls to the top, just
  // below the app's sticky header.
  const scrollChartIntoView = () => {
    const area = chartAreaRef.current
    if (!area) return
    const headerBottom =
      document.querySelector("header")?.getBoundingClientRect().bottom ?? 0
    const { top, bottom } = area.getBoundingClientRect()
    if (top >= headerBottom && bottom <= window.innerHeight) return
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    area.style.scrollMarginTop = `${Math.max(headerBottom, 0) + SCROLL_GAP}px`
    area.scrollIntoView({
      block: "start",
      behavior: reduceMotion ? "auto" : "smooth",
    })
  }

  // Pressing the selected row clears it; any other row replaces it. Returns
  // whether the press selected a behaviour.
  const toggleSelection = (pressed: IDashboardBehaviorSelection) => {
    if (isSameBehavior(selection, pressed)) {
      clearSelection()
      return false
    }
    select(pressed)
    setAnnouncement(
      t("adminDashboard.chartShowsBehaviour", {
        ...RAW_INTERPOLATION,
        behaviour: behaviorLabel(t, pressed),
      }),
    )
    return true
  }

  const handleSelect = (pressed: IDashboardBehaviorSelection) => {
    if (toggleSelection(pressed)) scrollChartIntoView()
  }

  // The chart scrolls into view once the dialogs have gone.
  const handleDialogSelect = (pressed: IDashboardBehaviorSelection) => {
    focusAfterDialogs.current = toggleSelection(pressed) ? "card" : "title"
    closeDialogs()
  }

  // MUI has already restored focus to "See all" by now, so this wins.
  const handleDialogsExited = () => {
    const target = focusAfterDialogs.current
    focusAfterDialogs.current = null
    if (target === "card") {
      scrollChartIntoView()
      clearButtonRef.current?.focus({ preventScroll: true })
    }
    if (target === "title") titleRef.current?.focus()
  }

  return {
    titleRef,
    chartAreaRef,
    clearButtonRef,
    selectedRowRef,
    announcement,
    handleClear,
    handleSelect,
    handleDialogSelect,
    handleDialogsExited,
  }
}

export default useBehaviorSelectionFocus
